import React, { useState } from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Collapse,
  Paper,
  Divider,
  makeStyles
} from "@material-ui/core";
import { Business, Security } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(3),
    color: theme.palette.primary.main,
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  formGrid: {
    marginBottom: theme.spacing(4),
  },
  conditionalSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  divider: {
    margin: theme.spacing(4, 0),
  },
  validationIcon: {
    fontSize: '1rem',
    marginLeft: theme.spacing(1),
  },
  successField: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.success.main,
      },
    },
  }
}));

const ClientIdPSetup = ({
  clientData,
  idpData,
  onClientUpdate,
  onIdPUpdate
}) => {
  const classes = useStyles();
  const [clientErrors, setClientErrors] = useState({});
  const [idpErrors, setIdpErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUrl = (url) => {
    if (!url) return true; // Optional field
    try {
      new URL(url);
      return url.startsWith('http://') || url.startsWith('https://');
    } catch {
      return false;
    }
  };

  const validateClientName = (name) => {
    return name && name.trim().length >= 2 && name.trim().length <= 50;
  };

  const validateClientCode = (code) => {
    const codeRegex = /^[a-zA-Z0-9_-]{2,20}$/;
    return code && codeRegex.test(code.trim());
  };

  const validateTenantId = (tenantId, provider) => {
    if (!tenantId || !tenantId.trim()) return false;

    if (provider === "Azure AD") {
      // Azure AD tenant ID (GUID format)
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      return guidRegex.test(tenantId.trim());
    }

    // For other providers, just check it's not empty and reasonable length
    return tenantId.trim().length >= 3 && tenantId.trim().length <= 100;
  };

  // Real-time validation
  const validateClientField = (field, value) => {
    const errors = {};

    switch (field) {
      case 'clientName':
        if (!value || !value.trim()) {
          errors.clientName = 'Client name is required';
        } else if (value.trim().length < 2) {
          errors.clientName = 'Client name must be at least 2 characters';
        } else if (value.trim().length > 50) {
          errors.clientName = 'Client name must not exceed 50 characters';
        }
        break;
      case 'clientCode':
        if (!value || !value.trim()) {
          errors.clientCode = 'Client code is required';
        } else if (!validateClientCode(value)) {
          errors.clientCode = 'Client code must be 2-20 characters (letters, numbers, - and _ only)';
        }
        break;
      case 'technicalContact':
        if (!value || !value.trim()) {
          errors.technicalContact = 'Technical contact email is required';
        } else if (!validateEmail(value.trim())) {
          errors.technicalContact = 'Please enter a valid email address';
        }
        break;
      case 'environment':
        if (!value) {
          errors.environment = 'Environment selection is required';
        }
        break;
      case 'hostingType':
        if (!value) {
          errors.hostingType = 'Hosting type selection is required';
        }
        break;
    }

    return errors;
  };

  const validateIdPField = (field, value) => {
    const errors = {};

    switch (field) {
      case 'provider':
        if (!value) {
          errors.provider = 'Identity provider selection is required';
        }
        break;
      case 'protocol':
        if (!value) {
          errors.protocol = 'Protocol selection is required';
        }
        break;
      case 'tenantId':
        // Made optional - only validate format if value is provided
        if (value && value.trim() && !validateTenantId(value, idpData.provider)) {
          if (idpData.provider === "Azure AD") {
            errors.tenantId = 'Please enter a valid Azure AD tenant ID (GUID format)';
          } else {
            errors.tenantId = 'Please enter a valid organization URL';
          }
        }
        break;
      case 'metadataUrl':
        if (value && !validateUrl(value)) {
          errors.metadataUrl = 'Please enter a valid URL starting with http:// or https://';
        }
        break;
    }

    return errors;
  };

  const handleClientChange = (field, value) => {
    // Update data
    onClientUpdate({
      ...clientData,
      [field]: value
    });

    // Mark field as touched
    setTouched(prev => ({ ...prev, [field]: true }));

    // Validate field if it's been touched
    if (touched[field] || value) {
      const fieldErrors = validateClientField(field, value);
      setClientErrors(prev => ({
        ...prev,
        ...fieldErrors,
        [field]: fieldErrors[field] || null
      }));
    }
  };

  const handleIdPChange = (field, value) => {
    // Update data
    onIdPUpdate({
      ...idpData,
      [field]: value
    });

    // Mark field as touched
    setTouched(prev => ({ ...prev, [field]: true }));

    // Validate field if it's been touched
    if (touched[field] || value) {
      const fieldErrors = validateIdPField(field, value);
      setIdpErrors(prev => ({
        ...prev,
        ...fieldErrors,
        [field]: fieldErrors[field] || null
      }));
    }

    // Clear tenant ID error if provider changes
    if (field === 'provider' && idpErrors.tenantId) {
      setIdpErrors(prev => ({ ...prev, tenantId: null }));
    }
  };

  const handleBlur = (section, field) => {
    setTouched(prev => ({ ...prev, [field]: true }));

    if (section === 'client') {
      const fieldErrors = validateClientField(field, clientData[field]);
      setClientErrors(prev => ({
        ...prev,
        ...fieldErrors
      }));
    } else {
      const fieldErrors = validateIdPField(field, idpData[field]);
      setIdpErrors(prev => ({
        ...prev,
        ...fieldErrors
      }));
    }
  };


  return (
    <Box className={classes.root}>
      {/* Client Setup Section */}
      <div className={classes.sectionHeader}>
        <Business className={classes.sectionIcon} />
        <Typography className={classes.sectionTitle}>
          Client Configuration
        </Typography>
      </div>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Configure basic client information for SSO integration.
      </Typography>

      <Grid container spacing={3} className={classes.formGrid}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Client Name"
            value={clientData.clientName || ''}
            onChange={(e) => handleClientChange('clientName', e.target.value)}
            onBlur={() => handleBlur('client', 'clientName')}
            variant="outlined"
            size="small"
            required
            error={!!clientErrors.clientName}
            helperText={clientErrors.clientName || 'Enter a descriptive name for your client'}
            placeholder="My Company Inc"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Client Code"
            value={clientData.clientCode || ''}
            onChange={(e) => handleClientChange('clientCode', e.target.value)}
            onBlur={() => handleBlur('client', 'clientCode')}
            variant="outlined"
            size="small"
            required
            error={!!clientErrors.clientCode}
            helperText={clientErrors.clientCode || 'Unique identifier (letters, numbers, - and _)'}
            placeholder="my-company"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl
            fullWidth
            variant="outlined"
            size="small"
            required
            error={!!clientErrors.environment}
          >
            <InputLabel>Environment</InputLabel>
            <Select
              value={clientData.environment || ''}
              onChange={(e) => handleClientChange('environment', e.target.value)}
              onBlur={() => handleBlur('client', 'environment')}
              label="Environment"
            >
              <MenuItem value="Dev">Development</MenuItem>
              <MenuItem value="UAT">UAT</MenuItem>
              <MenuItem value="Prod">Production</MenuItem>
            </Select>
          </FormControl>
          {clientErrors.environment && (
            <Typography variant="caption" color="error" style={{ marginLeft: 14, marginTop: 3 }}>
              {clientErrors.environment}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Technical Contact Email"
            type="email"
            value={clientData.technicalContact || ''}
            onChange={(e) => handleClientChange('technicalContact', e.target.value)}
            onBlur={() => handleBlur('client', 'technicalContact')}
            variant="outlined"
            size="small"
            required
            error={!!clientErrors.technicalContact}
            helperText={clientErrors.technicalContact || 'Primary contact for technical coordination'}
            placeholder="john.doe@company.com"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl
            fullWidth
            variant="outlined"
            size="small"
            required
            error={!!clientErrors.hostingType}
          >
            <InputLabel>Hosting Type</InputLabel>
            <Select
              value={clientData.hostingType || ''}
              onChange={(e) => handleClientChange('hostingType', e.target.value)}
              onBlur={() => handleBlur('client', 'hostingType')}
              label="Hosting Type"
            >
              <MenuItem value="Shared">Shared Hosting</MenuItem>
              <MenuItem value="Dedicated">Dedicated Hosting</MenuItem>
            </Select>
          </FormControl>
          {clientErrors.hostingType && (
            <Typography variant="caption" color="error" style={{ marginLeft: 14, marginTop: 3 }}>
              {clientErrors.hostingType}
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Hosting Type Information */}
      {clientData.hostingType && (
        <Paper className={classes.conditionalSection} elevation={0}>
          <Typography variant="subtitle2" style={{ fontWeight: 500, marginBottom: 8 }}>
            {clientData.hostingType} Hosting Selected
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {clientData.hostingType === "Shared"
              ? "You'll provide cluster details for each application in the next steps."
              : "You'll provide private DNS information for each application in the next steps."
            }
          </Typography>
        </Paper>
      )}

      <Divider className={classes.divider} />

      {/* Identity Provider Section */}
      <div className={classes.sectionHeader}>
        <Security className={classes.sectionIcon} />
        <Typography className={classes.sectionTitle}>
          Identity Provider Configuration
        </Typography>
      </div>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        Configure your Identity Provider settings for SSO integration.
      </Typography>

      <Grid container spacing={3} className={classes.formGrid}>
        <Grid item xs={12} sm={6}>
          <FormControl
            fullWidth
            variant="outlined"
            size="small"
            required
            error={!!idpErrors.provider}
          >
            <InputLabel>Identity Provider</InputLabel>
            <Select
              value={idpData.provider || ''}
              onChange={(e) => handleIdPChange('provider', e.target.value)}
              onBlur={() => handleBlur('idp', 'provider')}
              label="Identity Provider"
            >
              <MenuItem value="">Select Provider</MenuItem>
              <MenuItem value="Azure AD">Azure Active Directory</MenuItem>
              <MenuItem value="Okta">Okta</MenuItem>
              <MenuItem value="Ping Federate">Ping Federate</MenuItem>
              <MenuItem value="Other">Other Provider</MenuItem>
            </Select>
          </FormControl>
          {idpErrors.provider && (
            <Typography variant="caption" color="error" style={{ marginLeft: 14, marginTop: 3 }}>
              {idpErrors.provider}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl
            fullWidth
            variant="outlined"
            size="small"
            required
            error={!!idpErrors.protocol}
          >
            <InputLabel>Protocol</InputLabel>
            <Select
              value={idpData.protocol || ''}
              onChange={(e) => handleIdPChange('protocol', e.target.value)}
              onBlur={() => handleBlur('idp', 'protocol')}
              label="Protocol"
            >
              <MenuItem value="">Select Protocol</MenuItem>
              <MenuItem value="SAML">SAML 2.0</MenuItem>
              <MenuItem value="OIDC">OpenID Connect</MenuItem>
            </Select>
          </FormControl>
          {idpErrors.protocol && (
            <Typography variant="caption" color="error" style={{ marginLeft: 14, marginTop: 3 }}>
              {idpErrors.protocol}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={idpData.provider === "Azure AD" ? "Tenant ID" : "Organization URL"}
            value={idpData.tenantId || ''}
            onChange={(e) => handleIdPChange('tenantId', e.target.value)}
            onBlur={() => handleBlur('idp', 'tenantId')}
            variant="outlined"
            size="small"
            error={!!idpErrors.tenantId}
            helperText={idpErrors.tenantId || (idpData.provider === "Azure AD" ? "Your Azure AD tenant GUID (optional)" : "Your organization's domain or URL (optional)")}
            placeholder={idpData.provider === "Azure AD" ? "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" : "Enter your organization URL"}
          />
        </Grid>
      </Grid>

      {/* Protocol-specific configuration */}
      <Collapse in={idpData.protocol === "SAML"} timeout="auto" unmountOnExit>
        <Paper className={classes.conditionalSection} elevation={0}>
          <Typography variant="subtitle2" style={{ fontWeight: 500, marginBottom: 16, color: '#1976d2' }}>
            SAML Configuration
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Metadata URL (Optional)"
                value={idpData.metadataUrl || ''}
                onChange={(e) => handleIdPChange('metadataUrl', e.target.value)}
                onBlur={() => handleBlur('idp', 'metadataUrl')}
                variant="outlined"
                size="small"
                error={!!idpErrors.metadataUrl}
                helperText={idpErrors.metadataUrl || "URL to your IdP's SAML metadata endpoint (if available)"}
                placeholder="https://login.microsoftonline.com/tenant/FederationMetadata/2007-06/FederationMetadata.xml"
              />
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      <Collapse in={idpData.protocol === "OIDC"} timeout="auto" unmountOnExit>
        <Paper className={classes.conditionalSection} elevation={0}>
          <Typography variant="subtitle2" style={{ fontWeight: 500, marginBottom: 16, color: '#1976d2' }}>
            OpenID Connect Configuration
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Issuer URL"
                value={idpData.issuerUrl || ""}
                onChange={(e) => handleIdPChange('issuerUrl', e.target.value)}
                variant="outlined"
                size="small"
                required
                placeholder="https://login.microsoftonline.com/tenant/v2.0"
                helperText="The OIDC issuer URL from your identity provider"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Client ID (Optional)"
                value={idpData.clientId || ""}
                onChange={(e) => handleIdPChange('clientId', e.target.value)}
                variant="outlined"
                size="small"
                helperText="Leave blank for auto-generation during setup"
              />
            </Grid>
          </Grid>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default ClientIdPSetup;